/**
 * Chat-spawned background job outcomes, surfaced inline in the open thread.
 * Split out of `useAgentChat.ts` with no behavior change.
 */
import { useEffect, useRef } from 'react';

import { useNamespaceSocket } from '../../contexts/SocketContext';
import { statusMessageFromNotification, type JobOutcomeNotification } from './agentChatReducer';
import { generateId, type ChatCore } from './useAgentChatCore';

export function useAgentChatJobOutcomes(chat: ChatCore, sessionId: string): void {
  // Notifications socket — a chat-spawned background job posts its outcome
  // here tagged with the originating conversationId; when it matches THIS
  // open thread we surface it inline (see the effect below).
  const notificationsSocket = useNamespaceSocket('notifications');
  // De-dupe guard: a job emits one outcome event, but StrictMode re-mounts or
  // multi-room delivery could double-fire. Keyed by a stable event signature.
  const seenStatusRef = useRef<Set<string>>(new Set());

  // Surface a chat-spawned background job's outcome inline in THIS thread.
  // The job (author_long_document / equipment knowledge fetch) emits a
  // /notifications event tagged with the originating conversationId; when it
  // matches the open session we append a quiet status message. The global
  // toast listener still fires — this is purely additive, so the user is also
  // covered when the panel is closed. Best-effort: no match ⇒ nothing here.
  useEffect(() => {
    if (!notificationsSocket || !sessionId) return;
    const { setMessages } = chat;
    const handleOutcome = (event: JobOutcomeNotification): void => {
      const conversationId =
        typeof event.data?.conversationId === 'string' ? event.data.conversationId : '';
      if (conversationId !== sessionId) return;
      const signature = [
        event.type,
        conversationId,
        event.entityRef?.id ?? '',
        event.timestamp ?? event.message,
      ].join(':');
      if (seenStatusRef.current.has(signature)) return;
      const statusMessage = statusMessageFromNotification(event, generateId(), Date.now());
      if (!statusMessage) return;
      seenStatusRef.current.add(signature);
      setMessages((prev) => [...prev, statusMessage]);
    };
    notificationsSocket.on('notification', handleOutcome);
    return () => {
      notificationsSocket.off('notification', handleOutcome);
    };
  }, [chat, notificationsSocket, sessionId]);
}
